import {Observable, of} from 'rxjs';
import {catchError, map, startWith} from 'rxjs/operators';
import {CombinedGraphQLErrors} from '@apollo/client/errors';
import {errorText} from './error-link';

/** What a table or panel renders for one GraphQL load. */
export interface LoadState<T> {
  loading: boolean;
  data: T | null;
  /** Human-readable reason the load failed; null while loading or once loaded. */
  error: string | null;
}

export const LOADING: LoadState<any> = {loading: true, data: null, error: null};

/**
 * Turns one GraphQL request (query, watchQuery valueChanges, mutate) into a stream of load states:
 * loading first, then the data, or the error -- never an endless "Loading ...". A result that
 * carries GraphQL errors (errorPolicy 'all') counts as failed when it has no data. The error is
 * caught here, inside the request, so use it inside switchMap: the outer stream (route, reload
 * button, poll) survives and the next trigger loads again.
 *
 *   rows$ = reload$.pipe(switchMap(() => gqlLoad(apollo.query(...), r => r.data.roots)));
 */
export function gqlLoad<R, T>(request: Observable<R>, select: (result: R) => T): Observable<LoadState<T>> {
  return request.pipe(
    map(result => {
      const errors = (result as {error?: unknown})?.error;
      if (errors && (result as {data?: unknown}).data == null) {
        throw errors;
      }
      return {loading: false, data: select(result), error: null} as LoadState<T>;
    }),
    catchError(error => of({loading: false, data: null, error: errorText(error)} as LoadState<T>)),
    startWith(LOADING as LoadState<T>),
  );
}

/**
 * Rethrows a result that failed without data, so that catchError sees it. Apollo Client 4 reports
 * a failed watchQuery as an emission carrying `error` rather than as a stream error; a following
 * filter on data (or onlyCompleteData) would otherwise drop it silently. Put it before such filters.
 */
export function throwOnError<R>() {
  return map((result: R) => {
    const error = (result as {error?: unknown})?.error;
    if (error && (result as {data?: unknown}).data == null) {
      throw error;
    }
    return result;
  });
}

/** Whether an error is the API answering with GraphQL errors (as opposed to not reaching it). */
export function isGraphQLError(error: unknown): boolean {
  return CombinedGraphQLErrors.is(error);
}
